import { OnboardingAnswers } from "./onboardingStorage";

/**
 * The onboarding quiz as data: step order, options, and every piece of
 * per-answer tailored copy. Copy iteration happens here without touching the
 * renderers in steps/.
 *
 * Honesty rule (docs/mobile-onboarding-plan.md §3): the output is a tailored
 * STARTING PLAN — a mapping of the user's answers onto tools that exist in
 * the app today. Never claim the AI is trained/customized on their answers,
 * and never invent statistics.
 */

export type AnswerKey = keyof OnboardingAnswers;

export interface QuestionOption {
  key: string;
  label: string;
}

export type OnboardingStep =
  | { kind: "hook" }
  | { kind: "aiIntro" }
  | { kind: "commitment" }
  | { kind: "attPrimer" }
  | {
      kind: "question";
      answerKey: AnswerKey;
      title: string;
      subtitle?: string;
      options: QuestionOption[];
      multi?: boolean;
    }
  | { kind: "affirmation" }
  | { kind: "pain" }
  | { kind: "mission" }
  | { kind: "honesty" }
  | { kind: "showcase" }
  | { kind: "aiShowcase" }
  | { kind: "loader" }
  | { kind: "reveal" }
  | { kind: "ratingAsk" };

export const ONBOARDING_STEPS: OnboardingStep[] = [
  { kind: "hook" },
  { kind: "aiIntro" },
  { kind: "commitment" },
  { kind: "attPrimer" },
  {
    kind: "question",
    answerKey: "experience",
    title: "What best describes you?",
    subtitle: "Your plan starts from where you are today.",
    options: [
      { key: "starting", label: "Just starting out" },
      { key: "casual", label: "Investing casually" },
      { key: "experienced", label: "Experienced investor" },
      { key: "trader", label: "Active trader" },
    ],
  },
  {
    kind: "question",
    answerKey: "goal",
    title: "What's your main goal?",
    subtitle: "Everything in your plan works towards this.",
    options: [
      { key: "wealth", label: "Build long-term wealth" },
      { key: "income", label: "Create a second income" },
      { key: "outperform", label: "Beat the market" },
      { key: "learn", label: "Learn to invest properly" },
    ],
  },
  { kind: "affirmation" },
  {
    kind: "question",
    answerKey: "decide",
    title: "How do you decide what to buy today?",
    subtitle: "Be honest, and pick as many as apply. This is how most people start.",
    multi: true,
    options: [
      { key: "social", label: "Social media" },
      { key: "news", label: "News articles" },
      { key: "gut", label: "Gut feeling" },
      { key: "research", label: "My own research" },
      { key: "communities", label: "Online communities" },
      { key: "friends", label: "Friends & family advice" },
    ],
  },
  { kind: "pain" },
  {
    kind: "question",
    answerKey: "struggle",
    title: "What's hardest for you right now?",
    options: [
      { key: "what_to_buy", label: "Knowing what to buy" },
      { key: "when_to_sell", label: "Knowing when to sell" },
      { key: "financials", label: "Understanding the financials" },
      { key: "noise", label: "Too much noise and information" },
    ],
  },
  {
    kind: "question",
    answerKey: "time",
    title: "How much time can you give it each week?",
    subtitle: "Your plan fits around your life, not the other way round.",
    options: [
      { key: "low", label: "Under an hour" },
      { key: "medium", label: "1–3 hours" },
      { key: "high", label: "3+ hours" },
    ],
  },
  {
    kind: "question",
    answerKey: "markets",
    title: "What interests you most?",
    subtitle: "Pick as many as you like.",
    multi: true,
    options: [
      { key: "us", label: "US stocks" },
      { key: "uk", label: "UK stocks" },
      { key: "tech", label: "Tech & growth" },
      { key: "dividends", label: "Dividend income" },
      { key: "etfs", label: "ETFs & funds" },
      { key: "value", label: "Value investing" },
    ],
  },
  { kind: "mission" },
  { kind: "honesty" },
  { kind: "showcase" },
  { kind: "aiShowcase" },
  { kind: "loader" },
  { kind: "reveal" },
  { kind: "ratingAsk" },
];

/** Index of a step's question among all question steps (for the segmented
    progress bar), or -1 for non-question steps. */
export const questionIndexOf = (stepIndex: number): number => {
  if (ONBOARDING_STEPS[stepIndex]?.kind !== "question") return -1;
  return (
    ONBOARDING_STEPS.slice(0, stepIndex + 1).filter(
      (step) => step.kind === "question",
    ).length - 1
  );
};

/** Questions answered before (or at) a step — segments filled while
    interstitials hold the previous count so progress never stalls. */
export const questionsCompletedBefore = (stepIndex: number): number =>
  ONBOARDING_STEPS.slice(0, stepIndex).filter(
    (step) => step.kind === "question",
  ).length;

export const QUESTION_COUNT = ONBOARDING_STEPS.filter(
  (step) => step.kind === "question",
).length;

// --- Static screen copy ------------------------------------------------------

/** The welcome screen: brand, promise, orbit. Deliberately NOT a product
    demo — the app sells itself one screen later, once the user has opted in
    by tapping through. */
export const HOOK_COPY = {
  title: "Research stocks like the professionals do.",
  body: "Decades of financials, earnings calls, insider activity and AI analysis grounded in real filings, all available from your pocket.",
  cta: "Get started",
};

/**
 * First slide after the welcome: a live Insight AI demo. The copy narrates
 * the demo rather than repeating it — the demo already proves the grounding
 * ("scanning N earnings calls"), so the body sells the scope instead.
 *
 * `titleWords` types through in order and the LAST one is the general claim,
 * so it doubles as the static reduced-motion headline.
 */
export const AI_INTRO_COPY = {
  titlePrefix: "Real answers on",
  titleWords: [
    "Nvidia",
    "Microsoft",
    "Apple",
    "SpaceX",
    "Cerebras Systems",
    "AMD",
    "Any Public Company",
  ],
  body: "Professional-grade research on any stock you're considering.",
  cta: "Continue",
};

export const COMMITMENT_COPY = {
  title: "Most people guess when buying stocks.",
  body: "You're here because you want to see the data first, and actually understand a company before you invest.\n\nThat one habit separates the investors who succeed from those who don't.",
  cta: "I'm ready",
};

export const MISSION_COPY = {
  eyebrow: "Our mission",
  title: "This data used to cost thousands a year.",
  body: "Deep financial history, earnings-call transcripts, insider trades, institutional filings. Powerful agentic AI that answers your questions by searching tens of thousands of real earnings calls.\n\nFor decades data like this was the raw material of professional research, priced for institutions only.\n\nInsight exists to change that: the same tools, at a price an individual investor can actually justify, getting better every month.",
  cta: "Continue",
};

/**
 * Primes Apple's App Tracking Transparency prompt, which iOS shows straight
 * after this screen's CTA. Apple's rules: never incentivize the answer, never
 * gate anything on it, and never imply the system dialog can be avoided — the
 * flow continues identically either way.
 */
export const ATT_PRIMER_COPY = {
  eyebrow: "Before we start",
  title: "Help us make Insight better.",
  body: "Apple will ask on the next screen whether Insight can measure how people find and use the app.\n\nAllowing it helps us understand which features matter most, so we can keep improving Insight for everyone. It doesn't change anything about the app you get.",
  cta: "Continue",
};

export const HONESTY_COPY = {
  eyebrow: "Your answers",
  title: "A quick word on your answers.",
  body: "We use them to choose which of Insight's tools to show you first, and to start your watchlist with companies that match your interests.\n\nYour answers aren't shared with anyone, and they don't change the analysis. Every user gets the same AI, grounded in the same real filings and transcripts.",
  cta: "Sounds fair",
};

export const SHOWCASE_COPY = {
  eyebrow: "Real data",
  title: "Decades deep on every company you research.",
  body: "Real numbers from real filings: financial history, earnings-call transcripts, insider buys and sells, plus AI that explains what it all means.",
  cta: "Continue",
};

export const AI_SHOWCASE_COPY = {
  eyebrow: "Insight AI",
  title: "Ask questions no screener can answer.",
  body: "Insight AI searches every earnings call and filing to answer questions like this.",
  cta: "Continue",
};

export const REVEAL_COPY = {
  title: "Your starting plan",
  body: "Every item is a tool that's live in the app today.",
  cta: "Let's go",
};

/**
 * The rating ask, straight after the reveal: the funnel's peak-goodwill
 * moment (the plan just landed) and still before the paywall, so the majority
 * who never subscribe can leave a rating while they feel good, and nobody is
 * asked after the paywall has annoyed them.
 *
 * Apple rules (guideline 5.6.1): official requestReview sheet only, no
 * incentives, never name a star count, and the flow advances identically
 * whether or not the sheet appears — iOS caps it at 3 shows per user per 365
 * days and reports nothing back. The small-team framing is literal fact, not
 * theatre, which is what the honesty rule requires of it.
 */
export const RATING_COPY = {
  eyebrow: "A small favour",
  title: "We're a tiny team taking on the giants.",
  body: "Insight is a tiny team up against giant companies, building powerful research tools at a fraction of the price.\n\nIt would mean the world to us if you left a positive review. It helps Insight keep bringing affordable investing tools to retail investors like you.",
  cta: "Sure, I'll rate it",
  skip: "Maybe later",
};

// --- Tailored copy ---------------------------------------------------------

interface InterstitialCopy {
  title: string;
  body: string;
}

const AFFIRMATION_BY_GOAL: Record<string, InterstitialCopy> = {
  wealth: {
    title: "Long-term wealth is built, not found",
    body: "Good decisions compound the same way money does. Know the businesses you own and you give yourself the best shot at growth that actually lasts.",
  },
  income: {
    title: "A second income needs reliable businesses",
    body: "Income investing rewards the people who check cash flows, payout history and balance sheets before they buy. That's the whole point of starting from the data.",
  },
  outperform: {
    title: "Beating the market takes an edge",
    body: "Speed isn't your edge. Understanding a business better than the people guessing at it is, and that comes from the data.",
  },
  learn: {
    title: "Learning to invest properly pays for life",
    body: "Judging a business on its real numbers is a skill, and it compounds just like your money does. Every stock you research makes the next one easier.",
  },
};

const DEFAULT_AFFIRMATION: InterstitialCopy = AFFIRMATION_BY_GOAL.wealth;

export const affirmationCopy = (answers: OnboardingAnswers): InterstitialCopy =>
  AFFIRMATION_BY_GOAL[answers.goal ?? ""] ?? DEFAULT_AFFIRMATION;

/**
 * Pain interstitial: agitate, then absolve. The lead-in acknowledges how THEY
 * said they pick stocks; the body is the defensible core claim (most active
 * investors underperform — phrased qualitatively, no invented numbers). Users
 * who already do their own research get an access framing, not a tools-shaming
 * one — they didn't do anything wrong.
 */
const PAIN_LEAD_BY_DECIDE: Record<string, string> = {
  social: "Tips can start an idea. They shouldn't finish the decision.",
  communities: "Communities are great at surfacing ideas. Less great at checking them.",
  friends: "Advice from people you trust still deserves a look at the numbers.",
  news: "By the time it's a headline, the market has usually moved.",
  gut: "Instinct is real, but it works far better with numbers behind it.",
  research: "You're ahead of most already. The right tools just make research faster.",
};

/** Most-agitating source first — the lead acknowledges the guessiest habit
    they admitted to; the research lead only fires for research-only users. */
const PAIN_LEAD_PRIORITY = ["social", "communities", "friends", "news", "gut", "research"];

const PAIN_BODY_DEFAULT =
  "Study after study finds the same gap between guessing and research.\n\nIt comes down to the quality of information behind each decision, and most people have never had the tools the professionals use.";

const PAIN_BODY_RESEARCH =
  "Study after study finds the same gap between guessing and research.\n\nFor you the gap was never effort. It's access: the professionals' data has never been priced for individual investors.";

/** Old installs may have persisted `decide` as a single string. */
const decideList = (answers: OnboardingAnswers): string[] => {
  if (Array.isArray(answers.decide)) return answers.decide;
  if (answers.decide) return [answers.decide];
  return [];
};

export const painCopy = (answers: OnboardingAnswers): InterstitialCopy => {
  const decide = decideList(answers);
  const leadKey =
    PAIN_LEAD_PRIORITY.find((key) => decide.includes(key)) ?? "gut";
  const researchOnly = decide.length > 0 && decide.every((key) => key === "research");
  return {
    title: "Most self-directed investors underperform the market",
    body: `${PAIN_LEAD_BY_DECIDE[leadKey]}\n\n${
      researchOnly ? PAIN_BODY_RESEARCH : PAIN_BODY_DEFAULT
    }`,
  };
};

/** Loader stages personalized from the answers — the plan really is assembled
    from them, so naming them back is honest, not theatre. */
export const buildLoaderStages = (answers: OnboardingAnswers): string[] => {
  const stages: string[] = [];

  const goal = GOAL_LABELS[answers.goal ?? ""];
  stages.push(goal ? `Focusing on ${goal}` : "Reviewing your goals");

  const experience = EXPERIENCE_LABELS[answers.experience ?? ""];
  stages.push(
    experience
      ? `Matching tools for ${experience}`
      : "Matching the right research tools",
  );

  const markets = (answers.markets ?? [])
    .map((key) => MARKET_LABELS[key])
    .filter(Boolean)
    .slice(0, 2);
  if (markets.length > 0) stages.push(`Tuning for ${markets.join(" & ")}`);

  stages.push("Putting your starting plan together");
  return stages;
};

// --- The plan --------------------------------------------------------------

export interface PlanItem {
  title: string;
  body: string;
}

const STRUGGLE_ITEMS: Record<string, PlanItem> = {
  what_to_buy: {
    title: "Find your next stock with data",
    body: "Screen every market by the numbers that matter, then dig into institutional-grade financials, valuation charts and projections.",
  },
  when_to_sell: {
    title: "Know when the story changes",
    body: "Watchlist your companies and follow their earnings, valuation and news in one place, so selling becomes a data decision instead of a panicked one.",
  },
  financials: {
    title: "Let AI explain the numbers",
    body: "Ask Insight AI to break down any company's financials in plain English, grounded in the real filings.",
  },
  noise: {
    title: "Cut the noise",
    body: "Follow just your companies with the earnings calendar and curated news. Nothing else.",
  },
};

const DECIDE_ITEM: PlanItem = {
  title: "Check every idea against the data",
  body: "Before you buy, run any tip or headline through Insight's financials and AI analysis.",
};

const TIME_LOW_ITEM: PlanItem = {
  title: "Research in minutes, not hours",
  body: "AI stock reports give you the full picture on a company in minutes. Insight does the heavy lifting.",
};

const DEFAULT_ITEM: PlanItem = {
  title: "Ask Insight AI anything",
  body: "Get answers about any stock grounded in real financial data, earnings and filings. Not vibes.",
};

/**
 * Always 3–4 concrete, honest plan items. Answer-driven items lead; the
 * universal fillers pad the plan to three so the reveal never looks sparse
 * (every user wants Insight for these regardless of their answers). Every
 * item maps to a real feature that works on day one.
 */
export const buildPlanItems = (answers: OnboardingAnswers): PlanItem[] => {
  const items: PlanItem[] = [];

  const struggleItem = STRUGGLE_ITEMS[answers.struggle ?? ""];
  if (struggleItem) items.push(struggleItem);

  if (decideList(answers).some((key) => key !== "research")) {
    items.push(DECIDE_ITEM);
  }

  if (answers.time === "low") items.push(TIME_LOW_ITEM);

  // AI is the flagship — always on the plan unless the struggle item already
  // leads with it.
  if (answers.struggle !== "financials") items.push(DEFAULT_ITEM);

  const fillers = [
    DEFAULT_ITEM,
    STRUGGLE_ITEMS.what_to_buy,
    TIME_LOW_ITEM,
    STRUGGLE_ITEMS.noise,
  ];
  for (const filler of fillers) {
    if (items.length >= 3) break;
    if (!items.some((item) => item.title === filler.title)) items.push(filler);
  }

  return items.slice(0, 4);
};

const EXPERIENCE_LABELS: Record<string, string> = {
  starting: "a first-time investor",
  casual: "a casual investor",
  experienced: "an experienced investor",
  trader: "an active trader",
};

const GOAL_LABELS: Record<string, string> = {
  wealth: "building long-term wealth",
  income: "creating a second income",
  outperform: "beating the market",
  learn: "learning to invest properly",
};

const MARKET_LABELS: Record<string, string> = {
  us: "US stocks",
  uk: "UK stocks",
  tech: "tech & growth",
  dividends: "dividend income",
  etfs: "ETFs & funds",
  value: "value investing",
};

/** "For a casual investor focused on building long-term wealth" — or null. */
export const planSubject = (answers: OnboardingAnswers): string | null => {
  const experience = EXPERIENCE_LABELS[answers.experience ?? ""];
  const goal = GOAL_LABELS[answers.goal ?? ""];
  if (!experience && !goal) return null;
  if (!goal) return `For ${experience}`;
  if (!experience) return `For an investor focused on ${goal}`;
  return `For ${experience} focused on ${goal}`;
};

// --- Paywall personalization -----------------------------------------------

const PAYWALL_SUBTITLE_BY_GOAL: Record<string, string> = {
  wealth: "Let's build long-term wealth with data, not guesses",
  income: "Let's build that second income with data, not guesses",
  outperform: "Let's find your edge with data, not guesses",
  learn: "Let's make you a confident, data-driven investor",
};

export const paywallCopy = (
  answers: OnboardingAnswers | null,
): { title: string; subtitle: string } => {
  if (!answers) {
    return {
      title: "Upgrade to Insight Pro",
      subtitle: "Professional-grade analytics and AI for your research",
    };
  }
  return {
    title: "Your plan is ready",
    subtitle:
      PAYWALL_SUBTITLE_BY_GOAL[answers.goal ?? ""] ??
      PAYWALL_SUBTITLE_BY_GOAL.wealth,
  };
};
